import React, { useState } from 'react';
import { Select, MenuItem, Typography } from '@mui/material';
import { CalendarMonthOutlined } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers';
import { RoutineActivityRecurringDay, RoutineCustomRecurrence } from '@demind-inc/core';
import dayjs, { Dayjs } from 'dayjs';
import clsx from 'clsx';
import './CustomRecurrenceSelector.scss';
import {
  DAYS_OF_WEEK,
  DAY_FULL_LABELS,
  WEEK_POSITIONS,
  WEEKLY_INTERVAL_OPTIONS,
  MONTHLY_INTERVAL_OPTIONS,
  MAX_MONTH_DAY,
  buildDefaultCustomRecurrence,
} from '../recurrence.helpers';
import { useTranslation } from 'react-i18next';
import { getWeekdayLabels } from '../../../DataView/i18n';

interface CustomRecurrenceSelectorProps {
  value: RoutineCustomRecurrence;
  onChange: (value: RoutineCustomRecurrence) => void;
  disabled?: boolean;
}

const CustomRecurrenceSelector: React.FC<CustomRecurrenceSelectorProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [isStartDatePickerOpen, setIsStartDatePickerOpen] = useState(false);
  const isMonthly = value.type === 'monthly-date' || value.type === 'monthly-weekday';
  const startDateValue = dayjs(value.startDate);
  const normalizedStartDate = startDateValue.isValid() ? startDateValue : dayjs();
  const normalizedStartDateString = normalizedStartDate.format('YYYY-MM-DD');
  const normalizedValue = startDateValue.isValid()
    ? value
    : { ...value, startDate: normalizedStartDateString };

  const handleFrequencyChange = (freq: 'weekly' | 'monthly') => {
    if (freq === 'weekly' && value.type !== 'weekly') {
      onChange(buildDefaultCustomRecurrence('weekly', normalizedValue));
    } else if (freq === 'monthly' && !isMonthly) {
      onChange(buildDefaultCustomRecurrence('monthly-date', normalizedValue));
    }
  };

  const handleMonthlySubTypeChange = (subType: 'monthly-date' | 'monthly-weekday') => {
    if (value.type !== subType) {
      onChange(buildDefaultCustomRecurrence(subType, normalizedValue));
    }
  };

  const handleIntervalChange = (interval: number) => {
    switch (normalizedValue.type) {
      case 'weekly':
        return onChange({ ...normalizedValue, interval });
      case 'monthly-date':
        return onChange({ ...normalizedValue, interval });
      case 'monthly-weekday':
        return onChange({ ...normalizedValue, interval });
    }
  };

  const handleWeeklyDayToggle = (day: RoutineActivityRecurringDay) => {
    if (normalizedValue.type !== 'weekly') return;
    const current = normalizedValue.daysOfWeek;
    const updated = current.includes(day) ? current.filter((d) => d !== day) : [...current, day];
    if (updated.length === 0) return;
    onChange({ ...normalizedValue, daysOfWeek: updated });
  };

  const handleMonthlyDateToggle = (date: number) => {
    if (normalizedValue.type !== 'monthly-date') return;
    const current = normalizedValue.daysOfMonth;
    const updated = current.includes(date)
      ? current.filter((d) => d !== date)
      : [...current, date].sort((a, b) => a - b);
    if (updated.length === 0) return;
    onChange({ ...normalizedValue, daysOfMonth: updated });
  };

  const handleWeekOfMonthChange = (raw: number) => {
    if (normalizedValue.type !== 'monthly-weekday') return;
    const match = WEEK_POSITIONS.find((p) => p.value === raw);
    if (!match) return;
    onChange({ ...normalizedValue, weekOfMonth: match.value });
  };

  const handleDayOfWeekChange = (raw: string) => {
    if (normalizedValue.type !== 'monthly-weekday') return;
    const match = DAY_FULL_LABELS.find((d) => d.key === raw);
    if (!match) return;
    onChange({ ...normalizedValue, dayOfWeek: match.key });
  };

  const handleStartDateChange = (newValue: Dayjs | null) => {
    if (!newValue || !newValue.isValid()) return;
    onChange({ ...normalizedValue, startDate: newValue.format('YYYY-MM-DD') });
  };

  const intervalOptions =
    value.type === 'weekly' ? WEEKLY_INTERVAL_OPTIONS : MONTHLY_INTERVAL_OPTIONS;
  const unitLabel =
    value.type === 'weekly'
      ? t('routine.customRecurrence.weekUnit')
      : t('routine.customRecurrence.monthUnit');
  const narrowWeekdayLabels = getWeekdayLabels(t, 'narrow');

  return (
    <div className={clsx('custom-recurrence', { 'custom-recurrence--disabled': disabled })}>
      {/* Frequency: Weekly / Monthly */}
      <div className="custom-recurrence__freq-tabs">
        <button
          type="button"
          className={clsx('custom-recurrence__freq-tab', {
            'custom-recurrence__freq-tab--active': value.type === 'weekly',
          })}
          onClick={() => handleFrequencyChange('weekly')}
          disabled={disabled}
        >
          {t('routine.customRecurrence.weekly')}
        </button>
        <button
          type="button"
          className={clsx('custom-recurrence__freq-tab', {
            'custom-recurrence__freq-tab--active': isMonthly,
          })}
          onClick={() => handleFrequencyChange('monthly')}
          disabled={disabled}
        >
          {t('routine.customRecurrence.monthly')}
        </button>
      </div>

      {/* Monthly sub-type: By date / By weekday */}
      {isMonthly && (
        <div className="custom-recurrence__sub-tabs">
          <button
            type="button"
            className={clsx('custom-recurrence__sub-tab', {
              'custom-recurrence__sub-tab--active': value.type === 'monthly-date',
            })}
            onClick={() => handleMonthlySubTypeChange('monthly-date')}
            disabled={disabled}
          >
            {t('routine.customRecurrence.byDate')}
          </button>
          <button
            type="button"
            className={clsx('custom-recurrence__sub-tab', {
              'custom-recurrence__sub-tab--active': value.type === 'monthly-weekday',
            })}
            onClick={() => handleMonthlySubTypeChange('monthly-weekday')}
            disabled={disabled}
          >
            {t('routine.customRecurrence.byWeekday')}
          </button>
        </div>
      )}

      {/* Interval */}
      <div className="custom-recurrence__interval-row">
        <Typography className="custom-recurrence__field-label">
          {t('routine.customRecurrence.every')}
        </Typography>
        <Select
          value={value.interval}
          onChange={(e) => handleIntervalChange(Number(e.target.value))}
          className="custom-recurrence__interval-select"
          disabled={disabled}
          size="small"
          variant="outlined"
        >
          {intervalOptions.map((n) => (
            <MenuItem key={n} value={n}>
              {n}
            </MenuItem>
          ))}
        </Select>
        <Typography className="custom-recurrence__field-label">{unitLabel}</Typography>
      </div>

      <div className="custom-recurrence__start-row">
        <div className="custom-recurrence__start-label">
          <CalendarMonthOutlined className="custom-recurrence__start-icon" />
          <Typography className="custom-recurrence__field-label">
            {t('routine.customRecurrence.starts')}
          </Typography>
        </div>
        <div className="custom-recurrence__start-picker-container">
          <DatePicker
            open={isStartDatePickerOpen}
            onClose={() => setIsStartDatePickerOpen(false)}
            value={normalizedStartDate}
            format="MMM D, YYYY"
            onChange={handleStartDateChange}
            className="custom-recurrence__start-picker"
            disabled={disabled}
            slotProps={{
              popper: {
                className: 'custom-time-picker-popper custom-time-picker-popper--auto-height',
              },
              textField: {
                variant: 'standard',
                disabled,
                inputProps: {
                  readOnly: true,
                },
                InputProps: {
                  disableUnderline: true,
                },
                onClick: () => {
                  if (!disabled) {
                    setIsStartDatePickerOpen(true);
                  }
                },
              },
              openPickerIcon: { style: { display: 'none' } },
            }}
          />
        </div>
      </div>

      {/* Weekly day pills */}
      {value.type === 'weekly' && (
        <div className="custom-recurrence__days-container">
          {DAYS_OF_WEEK.map(({ key }, index) => (
            <div
              key={key}
              onClick={() => !disabled && handleWeeklyDayToggle(key)}
              className={clsx('custom-recurrence__day-button', {
                'custom-recurrence__day-button--selected': value.daysOfWeek.includes(key),
                'custom-recurrence__day-button--disabled': disabled,
              })}
            >
              <Typography
                className={clsx('custom-recurrence__day-text', {
                  'custom-recurrence__day-text--selected': value.daysOfWeek.includes(key),
                })}
                variant="body1"
              >
                {narrowWeekdayLabels[index]}
              </Typography>
            </div>
          ))}
        </div>
      )}

      {/* Monthly date grid */}
      {value.type === 'monthly-date' && (
        <div className="custom-recurrence__dates-grid">
          {Array.from({ length: MAX_MONTH_DAY }, (_, i) => i + 1).map((date) => (
            <div
              key={date}
              onClick={() => !disabled && handleMonthlyDateToggle(date)}
              className={clsx('custom-recurrence__date-button', {
                'custom-recurrence__date-button--selected': value.daysOfMonth.includes(date),
                'custom-recurrence__date-button--disabled': disabled,
              })}
            >
              <Typography
                className={clsx('custom-recurrence__date-text', {
                  'custom-recurrence__date-text--selected': value.daysOfMonth.includes(date),
                })}
                variant="body2"
              >
                {date}
              </Typography>
            </div>
          ))}
        </div>
      )}

      {/* Monthly weekday selectors */}
      {value.type === 'monthly-weekday' && (
        <div className="custom-recurrence__weekday-row">
          <Typography className="custom-recurrence__field-label">
            {t('routine.customRecurrence.onThe')}
          </Typography>
          <Select
            value={value.weekOfMonth}
            onChange={(e) => handleWeekOfMonthChange(Number(e.target.value))}
            className="custom-recurrence__position-select"
            disabled={disabled}
            size="small"
            variant="outlined"
          >
            {WEEK_POSITIONS.map(({ value: val }) => (
              <MenuItem key={val} value={val}>
                {t(`routine.recurrence.weekPositions.${val}`)}
              </MenuItem>
            ))}
          </Select>
          <Select
            value={value.dayOfWeek}
            onChange={(e) => handleDayOfWeekChange(String(e.target.value))}
            className="custom-recurrence__dayofweek-select"
            disabled={disabled}
            size="small"
            variant="outlined"
          >
            {DAY_FULL_LABELS.map(({ key }) => (
              <MenuItem key={key} value={key}>
                {t(`routine.weekdays.full.${key}`)}
              </MenuItem>
            ))}
          </Select>
        </div>
      )}
    </div>
  );
};

export default CustomRecurrenceSelector;
